import { Box, Grid, Skeleton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { subDays } from 'date-fns';
import KpiToggle, { useKPIToggle } from './ExploreKpiCards';
import ExploreChart from './ExploreChart';
import ExploreTable from './ExploreTable';
import { countryFilterFor } from './countryLabel';
import { CountryTabLabel } from './CountryTabLabel';
import { PAGE_PADDING_X, EXPLORE_COLORS } from './constants';
import { RootState } from 'src/context/store';
import { getLast12WeeksDateRange } from 'src/utils/format-time';
import { CountryBar } from 'src/components/country-bar';
import { CountryItem } from 'src/components/country-bar/types';
import { HEADER } from 'src/layouts/config-layout';
import { useCountryData } from 'src/components/visibility/hooks/useCountryData';
import {
  setExtraOptionsLoaded,
  setFilterCompare,
  setFilterCountry,
  setFilterDate,
  setFilterDateCompare,
  setFilterLoadedCountry,
  setFilterLoadedDevice,
  setFilterLoadedKeyword,
  setFilterLoadedPlatform,
  setFilterMinDatePicker,
  setFilterTimestep,
} from 'src/context/reducers/filter-settings/filterSlice';

export default function ExploreView() {
  const filterStore = useSelector((state: RootState) => state.filters);
  const dispatch = useDispatch();
  const toggle = useKPIToggle(
    filterStore.extraOptions?.mode === 'activations'
      ? ['ad_brand_traffic', 'cpc']
      : ['brand_searches', 'total_brand_traffic']
  );

  // Pays + coverage % via la query Visibility (front-only). Triés couverture desc.
  const { countriesData, isLoading: isLoadingCountries } = useCountryData();
  // Liste FIGÉE au 1er chargement : useCountryData relit filterStore.countries, donc dès qu'on
  // sélectionne un pays elle se réduirait à ce seul pays et le bandeau disparaîtrait
  // (CountryBar renvoie null si <= 1 pays). On fige la liste complète (coverage % sur l'ensemble).
  const [countryList, setCountryList] = useState<CountryItem[]>([]);
  // Défaut à l'atterrissage : l'onglet « All » (vue agrégée tous pays), comme le Funnel.
  // La CountryBar utilise le sentinel 'ALL' (cf. son mode includeAll).
  const [selectedCountry, setSelectedCountry] = useState<string | null>('ALL');

  // Au 1er chargement : fige la liste complète et applique le filtre « All ».
  // Le endpoint /data filtre par NOM de pays (region_name), pas par code — cf. le consumer
  // canonique useCountryData dans visibility/search-terms.tsx. countryFilterFor envoie la
  // liste complète pour « All » (un tableau vide renverrait 0 ligne via IN UNNEST).
  useEffect(() => {
    if (countryList.length === 0 && countriesData.length > 0) {
      setCountryList(countriesData);
      const allNames = countriesData.map((c) => c.country_name);
      dispatch(setFilterCountry(countryFilterFor('ALL', allNames)));
    }
  }, [countriesData, countryList.length, dispatch]);

  const handleCountryChange = (countryName: string | null) => {
    if (countryName == null) return;
    setSelectedCountry(countryName);
    const allNames = countryList.map((c) => c.country_name);
    dispatch(setFilterCountry(countryFilterFor(countryName, allNames)));
  };

  useEffect(() => {
    if (filterStore?.searchPerfDateOverridden) return; // keep the persisted override's timestep/alignment
    dispatch(setFilterTimestep('year_week'));
  }, []);

  useEffect(() => {
    if (toggle.statsData) dispatch(setFilterMinDatePicker('2024-01-01'));
  }, [toggle.statsData, dispatch]);

  useEffect(() => {
    if (toggle.statsData?.filters?.keywords) {
      dispatch(
        setFilterLoadedKeyword(
          toggle.statsData.filters.keywords.map((k: any) => ({
            search_term: k.keyword,
            total_organic_searches: k.total_organic_searches,
          }))
        )
      );
    }
    if (toggle.statsData?.filters?.devices)
      dispatch(setFilterLoadedDevice(toggle.statsData.filters.devices));
    if (toggle.statsData?.filters?.countries)
      dispatch(setFilterLoadedCountry(toggle.statsData.filters.countries));
    if (toggle.statsData?.filters?.platforms) {
      dispatch(
        setFilterLoadedPlatform(
          toggle.statsData.filters.platforms.map(
            (p: any) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
          )
        )
      );
    }
  }, [toggle.statsData?.filters, dispatch]);

  useEffect(() => {
    dispatch(setExtraOptionsLoaded(toggle.statsData?.filters));
  }, [toggle.statsData?.filters, dispatch]);

  useEffect(() => {
    if (filterStore?.searchPerfDateOverridden) return; // keep the persisted override
    const { start, end } = getLast12WeeksDateRange();
    dispatch(setFilterDate({ start, end }));
    dispatch(setFilterCompare(false));
    dispatch(
      setFilterDateCompare({
        compareStart: subDays(new Date(), 60).toISOString(),
        compareEnd: subDays(new Date(), 31).toISOString(),
      })
    );
  }, []);

  const loadingTop = toggle.isLoadingStats || toggle.isLoadingCompareStats || !toggle.statsData;

  // La query KPI (statsData) est indexée sur `filterStore.countries` : changer de pays crée une
  // NOUVELLE entrée de cache RTK (statsData=undefined + isLoadingStats=true le temps du fetch), donc
  // `loadingTop` repasse à true à chaque changement de pays. Sans ce verrou, ExploreTable serait
  // DÉMONTÉE (skeleton) à chaque interaction et perdrait tout son état de grille (colonnes ajoutées,
  // filtres, tri) — la rétention interne d'ExploreTable ne peut rien y faire, le parent la détruit.
  // On garde donc la table montée dès qu'elle a chargé une première fois ; ses propres overlays de
  // chargement couvrent les refetch d'arrière-plan.
  const [tableEverLoaded, setTableEverLoaded] = useState(false);
  useEffect(() => {
    if (!loadingTop) setTableEverLoaded(true);
  }, [loadingTop]);

  return (
    <Box px="24px">
      <CountryBar
        countries={countryList}
        selectedCountry={selectedCountry}
        onCountryChange={handleCountryChange}
        includeAll
        loading={isLoadingCountries && countryList.length === 0}
        // +6 vs the old HEADER.H_DESKTOP*3: the secondary nav grew (title 52→54,
        // filter bar 52→56), so the bar and its content spacer shift down to match.
        stickyTop={HEADER.H_DESKTOP * 3 + 6}
        topGap={60}
        contentPaddingX={PAGE_PADDING_X}
        selectedColor={EXPLORE_COLORS.countryUnderline}
        selectedBorderWidth={2}
        renderCountryLabel={(c, isSelected) => (
          <CountryTabLabel country={c} isSelected={isSelected} />
        )}
      />
      {loadingTop ? (
        <Box display="flex" flexDirection="row" mb={2}>
          <Grid container spacing={2}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Grid key={index} item sx={{ flex: '0 0 19.8%', minWidth: 210 }}>
                <Skeleton
                  width="100%"
                  height={100}
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    borderTopLeftRadius: index === 0 ? '12px' : 0,
                    borderBottomLeftRadius: index === 0 ? '12px' : 0,
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Skeleton
            width="3%"
            height={100}
            variant="rectangular"
            animation="wave"
            sx={{ borderTopRightRadius: '12px', borderBottomRightRadius: '12px' }}
          />
        </Box>
      ) : (
        <div style={{ position: 'relative' }}>
          <KpiToggle {...toggle} />
        </div>
      )}

      {loadingTop ? (
        <Skeleton variant="rectangular" animation="wave" width="100%" height={403} />
      ) : (
        <ExploreChart toggle={toggle} filterStore={filterStore} />
      )}

      {loadingTop && !tableEverLoaded ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="100%"
          height={403}
          sx={{ borderRadius: 2, mt: 2 }}
        />
      ) : (
        <ExploreTable />
      )}
    </Box>
  );
}
